import { Flex, Hint, Label, Link, Toggle } from '@lokalise/louis';
import { Form, setNestedObjectValues, useFormikContext } from 'formik';
import { useEffect } from 'react';

import SelectField from '@app/components/form/SelectField';

import { SplitView, SwitchWrapper } from '../styles';
import type { AddExistingUsersFormProps, AddExistingUsersFormSubmitValues, NumberSelectOption } from '../types';
import { LanguagesFields, Role } from '../types';
import { handleFormLanguageChanges } from '../utils';

const AddExistingUsersForm = ({
    project,
    teamMembers,
    isSubmitting,
    onValidate,
    onFormChange,
}: AddExistingUsersFormProps) => {
    const { values, setValues, setFieldValue, validateForm, setTouched, submitForm } =
        useFormikContext<AddExistingUsersFormSubmitValues>();

    const languageOptions = project.languages.map((language) => ({
        value: language.id,
        label: language.name,
    }));

    const onLanguageChange = (field: LanguagesFields, languages: readonly NumberSelectOption[]) => {
        const { referenceLanguages, contributableLanguages } = handleFormLanguageChanges({ field, languages, values });
        setValues({ ...values, contributableLanguages, referenceLanguages });
    };

    const validate = async () => {
        const formErrors = await validateForm();
        setTouched(setNestedObjectValues(formErrors, true));
        const isReady = Object.keys(formErrors).length === 0;

        onValidate(isReady);
        if (isReady) {
            await submitForm();
        }
    };

    useEffect(() => {
        if (values.role.value === Role.admin) {
            setFieldValue(LanguagesFields.contributableLanguages, null);
            setFieldValue(LanguagesFields.referenceLanguages, null);
        }
    }, [setFieldValue, values.role]);

    useEffect(() => {
        if (isSubmitting) {
            validate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitting]);

    useEffect(() => {
        onFormChange(values);
    }, [onFormChange, values]);

    return (
        <Form data-testid="addExistingUsersForm">
            <Flex gap={6}>
                <Label for="teamMembers" id="teamMembers" text="Team members">
                    <SelectField name="teamMembers" aria-labelledby="teamMembers" options={teamMembers} isMulti />
                </Label>
                <SplitView>
                    <Label
                        id="Role"
                        text={
                            <>
                                Role{' '}
                                <Hint ariaLabel="Role hint">
                                    You can adjust administrative rights after you&apos;ve invited the user.{' '}
                                    <Link
                                        href="https://docs.lokalise.com/translation-workflow/contributors?_ga=2.10561971.624071806.1644913872-624637374.1609319460"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Learn more.
                                    </Link>
                                </Hint>
                            </>
                        }
                        underline
                    >
                        <SelectField
                            aria-labelledby="Role"
                            name="role"
                            isSearchable={false}
                            options={[
                                { value: Role.translator, label: 'Translator' },
                                { value: Role.admin, label: 'Administrator' },
                            ]}
                        />
                    </Label>
                    <Label
                        text={
                            <>
                                Reviewer{' '}
                                <Hint ariaLabel="Reviewer hint">Can mark translations as reviewed in the editor.</Hint>
                            </>
                        }
                        underline
                    >
                        <SwitchWrapper>
                            <Toggle
                                checked={values.isReviewer}
                                onChange={() => setFieldValue('isReviewer', !values.isReviewer)}
                            />
                        </SwitchWrapper>
                    </Label>
                </SplitView>
                <Flex gap={4}>
                    <Label text="Languages" underline />
                    <Label
                        for="ViewableLanguage"
                        id="ViewableLanguage"
                        text={
                            <>
                                Reference languages (view){' '}
                                <Hint ariaLabel="Reference languages hint">Read-only access to these languages.</Hint>
                            </>
                        }
                    >
                        <SelectField
                            aria-labelledby="ViewableLanguage"
                            placeholder="Viewable languages"
                            name="referenceLanguages"
                            isDisabled={values.role.value === Role.admin}
                            options={languageOptions}
                            onChange={(value) => onLanguageChange(LanguagesFields.referenceLanguages, value)}
                            isMulti
                        />
                    </Label>
                    <Label
                        for="ContributableLanguage"
                        id="ContributableLanguage"
                        text={
                            <>
                                Contributable languages (edit){' '}
                                <Hint ariaLabel="Contributable languages hint">
                                    Read-write access to these languages.
                                </Hint>
                            </>
                        }
                    >
                        <SelectField
                            aria-labelledby="ContributableLanguage"
                            placeholder="Editable languages"
                            name="contributableLanguages"
                            isDisabled={values.role.value === Role.admin}
                            options={languageOptions}
                            onChange={(value) => onLanguageChange(LanguagesFields.contributableLanguages, value)}
                            isMulti
                        />
                    </Label>
                </Flex>
            </Flex>
        </Form>
    );
};

export default AddExistingUsersForm;
